import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Check, Copy, ExternalLink } from 'lucide-react';
import Layout from '@/components/Layout';
import {
  AntigravityLogo,
  ChatGptLogo,
  ClaudeLogo,
  CursorLogo,
  GeminiLogo,
  GrokLogo,
  OtherToolsLogo,
} from '@/components/icons/AiProviderLogos';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  CONNECT_AI_GROUPS,
  codingToolsSetupPrompt,
  connectAiGuides,
  type ConnectAiGuide,
  type ConnectAiGuideGroup,
  type ConnectAiGuideId,
} from '@/lib/connectAi/guides';
import { isClipMcpConnectPageEnabled } from '@/lib/clipMcpFlag';
import { formatDateTime } from '@/lib/format';
import {
  MY_CLIP_MCP_GRANTS,
  REVOKE_MY_CLIP_MCP_GRANT,
  type ClipMcpGrant,
} from '@/queries/queries';
import NotFound from './NotFound';

const LOGOS: Record<ConnectAiGuideId, typeof ChatGptLogo> = {
  chatgpt: ChatGptLogo,
  claude: ClaudeLogo,
  grok: GrokLogo,
  gemini: GeminiLogo,
  'claude-code': ClaudeLogo,
  codex: ChatGptLogo,
  cursor: CursorLogo,
  antigravity: AntigravityLogo,
  other: OtherToolsLogo,
};

const LOGO_CLASS: Record<ConnectAiGuideId, string> = {
  chatgpt: 'h-5 w-5 text-foreground',
  claude: 'h-5 w-5 text-[#D97757]',
  grok: 'h-5 w-5 text-foreground',
  gemini: 'h-5 w-5',
  'claude-code': 'h-5 w-5 text-[#D97757]',
  codex: 'h-5 w-5 text-foreground',
  cursor: 'h-5 w-5 text-foreground',
  antigravity: 'h-5 w-5 text-foreground',
  other: 'h-5 w-5 text-foreground',
};

const copyText = async (value: string) => {
  await navigator.clipboard.writeText(value);
};

const GuidePanel = ({
  guide,
  copied,
  onCopyCommand,
}: {
  guide: ConnectAiGuide;
  copied: boolean;
  onCopyCommand: () => void;
}) => {
  const Logo = LOGOS[guide.id];
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/60">
          <Logo className={LOGO_CLASS[guide.id]} label={guide.title} />
        </span>
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-foreground">{guide.title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{guide.need}</p>
        </div>
      </div>

      {guide.warning && (
        <Alert>
          <AlertDescription>{guide.warning}</AlertDescription>
        </Alert>
      )}

      <ol className="list-decimal space-y-2 pl-5 text-sm text-foreground">
        {guide.steps.map((step) => (
          <li key={step} className="pl-1 leading-6">{step}</li>
        ))}
      </ol>

      {guide.command && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
          <pre className="min-w-0 flex-1 overflow-x-auto rounded-md bg-muted px-3 py-2 text-sm">
            <code className="whitespace-pre-wrap break-all">{guide.command}</code>
          </pre>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 gap-2"
            onClick={() => {
              void onCopyCommand();
            }}
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? 'Copied' : (guide.copyLabel || 'Copy')}
          </Button>
        </div>
      )}

      <div>
        <p className="text-sm font-medium text-foreground">After it connects</p>
        <ol className="mt-2 list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
          {guide.after.map((step) => (
            <li key={step} className="pl-1 leading-6">{step}</li>
          ))}
        </ol>
      </div>

      {guide.docsHref && (
        <a
          href={guide.docsHref}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          {guide.docsLabel || 'Open official docs'}
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      )}
    </div>
  );
};

const ConnectAi = () => {
  const enabled = isClipMcpConnectPageEnabled();
  const { data, loading, refetch } = useQuery<{ myClipMcpGrants: ClipMcpGrant[] }>(
    MY_CLIP_MCP_GRANTS,
    { skip: !enabled },
  );
  const [revokeGrant, { loading: revoking }] = useMutation<
    { revokeMyClipMcpGrant: ClipMcpGrant | null },
    { id: string }
  >(REVOKE_MY_CLIP_MCP_GRANT);
  const [copied, setCopied] = useState<string | null>(null);
  const [group, setGroup] = useState<ConnectAiGuideGroup>('chat');

  if (!enabled) {
    return <NotFound />;
  }

  const mcpUrl = `${import.meta.env.VITE_API_URL}/integrations/clips/mcp`;
  const guides = connectAiGuides(mcpUrl);
  const toolsSetupPrompt = codingToolsSetupPrompt(mcpUrl);
  const grants = data?.myClipMcpGrants ?? [];

  const onRevoke = async (id: string) => {
    await revokeGrant({ variables: { id } });
    await refetch();
  };

  const markCopied = (key: string) => {
    setCopied(key);
    window.setTimeout(() => {
      setCopied((current) => (current === key ? null : current));
    }, 2000);
  };

  const onCopyUrl = async () => {
    try {
      await copyText(mcpUrl);
      markCopied('url');
    } catch {
      setCopied(null);
    }
  };

  const onCopyToolsPrompt = async () => {
    try {
      await copyText(toolsSetupPrompt);
      markCopied('coding-tools-prompt');
    } catch {
      setCopied(null);
    }
  };

  const onCopyGuide = async (guide: ConnectAiGuide) => {
    if (!guide.command) return;
    try {
      await copyText(guide.command);
      markCopied(guide.id);
    } catch {
      setCopied(null);
    }
  };

  return (
    <Layout>
      <div className="flex-1 p-4 lg:p-6">
        <div className="mb-6">
          <h2 className="text-xl font-bold text-foreground lg:text-2xl">Connect AI</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Link a chat app or a coding tool to this Autoclipper account. Copy the
            MCP URL, add Autoclipper as a custom connector or MCP server, then sign
            in when the client opens Autoclipper.
          </p>
        </div>

        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader>
              <CardTitle>MCP URL</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <code className="block min-w-0 flex-1 break-all rounded-md bg-muted px-3 py-2 text-sm">
                  {mcpUrl}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-2"
                  onClick={() => {
                    void onCopyUrl();
                  }}
                >
                  {copied === 'url' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied === 'url' ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
                <li>Copy the URL above.</li>
                <li>Add Autoclipper as a custom connector or remote MCP server.</li>
                <li>Sign in to Autoclipper and click Allow.</li>
                <li>Enable Autoclipper in a new chat, then paste a YouTube URL.</li>
              </ol>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>How to connect</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs
                value={group}
                onValueChange={(value) => {
                  setGroup(value as ConnectAiGuideGroup);
                }}
              >
                <TabsList className="mb-4">
                  {CONNECT_AI_GROUPS.map((item) => (
                    <TabsTrigger key={item.id} value={item.id}>
                      {item.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {CONNECT_AI_GROUPS.map((item) => {
                  const items = guides.filter((guide) => guide.group === item.id);
                  return (
                    <TabsContent key={item.id} value={item.id} className="space-y-6">
                      {item.id === 'tools' && (
                        <div className="flex flex-col gap-3 rounded-md border border-border p-4">
                          <div>
                            <h3 className="text-lg font-semibold text-foreground">
                              Set up every coding tool
                            </h3>
                            <p className="mt-1 text-sm text-muted-foreground">
                              Open each coding tool you use, paste this prompt, and send
                              it. The assistant configures Autoclipper in that tool only
                              and skips it if it is already connected.
                            </p>
                          </div>
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                            <pre className="min-w-0 flex-1 overflow-x-auto rounded-md bg-muted px-3 py-2 text-sm">
                              <code className="whitespace-pre-wrap break-all">
                                {toolsSetupPrompt}
                              </code>
                            </pre>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="shrink-0 gap-2"
                              onClick={() => {
                                void onCopyToolsPrompt();
                              }}
                            >
                              {copied === 'coding-tools-prompt'
                                ? <Check className="h-4 w-4" />
                                : <Copy className="h-4 w-4" />}
                              {copied === 'coding-tools-prompt' ? 'Copied' : 'Copy prompt'}
                            </Button>
                          </div>
                        </div>
                      )}
                      <Tabs defaultValue={items[0].id}>
                        <TabsList className="mb-4 flex h-auto w-full flex-wrap justify-start gap-1">
                          {items.map((guide) => {
                            const Logo = LOGOS[guide.id];
                            return (
                              <TabsTrigger
                                key={guide.id}
                                value={guide.id}
                                className="gap-2"
                              >
                                <span aria-hidden="true">
                                  <Logo
                                    className={LOGO_CLASS[guide.id]}
                                    label={guide.title}
                                  />
                                </span>
                                {guide.title}
                              </TabsTrigger>
                            );
                          })}
                        </TabsList>
                        {items.map((guide) => (
                          <TabsContent key={guide.id} value={guide.id}>
                            <GuidePanel
                              guide={guide}
                              copied={copied === guide.id}
                              onCopyCommand={() => {
                                void onCopyGuide(guide);
                              }}
                            />
                          </TabsContent>
                        ))}
                      </Tabs>
                    </TabsContent>
                  );
                })}
              </Tabs>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Connected assistants</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {loading ? (
                <p className="text-sm text-muted-foreground">Loading grants…</p>
              ) : grants.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No connected assistants yet. After you Allow Autoclipper, the
                  assistant appears here and you can revoke it.
                </p>
              ) : (
                grants.map((item) => (
                  <div
                    key={item.id}
                    className={
                      'flex flex-col gap-2 rounded-md border border-border p-3 '
                      + 'sm:flex-row sm:items-center sm:justify-between'
                    }
                  >
                    <div>
                      <p className="font-medium text-foreground">{item.label || item.id}</p>
                      <p className="text-xs text-muted-foreground">
                        Expires {formatDateTime(item.expires_at) || '—'}
                        {item.last_used_at
                          ? ` · Last used ${formatDateTime(item.last_used_at)}`
                          : ''}
                        {item.revoked_at ? ' · Revoked' : ''}
                      </p>
                    </div>
                    {!item.revoked_at && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={revoking}
                        onClick={() => {
                          void onRevoke(item.id);
                        }}
                      >
                        Revoke
                      </Button>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default ConnectAi;
